import React, { useState, useEffect, useLayoutEffect, useRef, useContext } from 'react';
import mapboxgl from 'mapbox-gl';
import { DateTime, Duration } from 'luxon';
import styled from 'styled-components';
import useSize from '@react-hook/size';
import useDebouncedEffect from 'use-debounced-effect';
import { debounce } from 'lodash';
import { LoadingOutlined } from '@ant-design/icons';
import { ColorMapLegend } from '@metservicedev/met-ui/wxtiles';

import { toHash } from '@mono/to-hash';
import { mapboxAccessTokenPk } from '@mono/mapbox-service-api';
import { usePlatmapContext } from '@mono/platmap-context';
import type { Layer, WXTilesLayer } from '@mono/types/src/platmap';
import { normalizeLongitude180, normalizeLongitude360 } from '@mono/normalize-longitude/src/normalize-longitude';
import { useThrottleState } from '@mono/use-throttle-state';
import { useSettingsContext } from '@mono/platmap-settings-context';
import { useUserTracking } from '@mono/user-tracking';
import { usePersistedState } from '@mono/use-persisted-state';

import { delimiter, anchorId } from './constants';
import baseStyle from './plat_blank';
import { MapboxServiceContext } from './mapbox-service-context/MapboxServiceContext';
import { WXTilesServiceContext } from './wxtiles-service-context/WXTilesServiceContext';
import { diff, getLegendUrl } from './magic';
import { getLayerIndex } from './getLayerIndex';
import { WXTilesV3ServiceContext } from 'wxtilesv3-service-context/WXTilesV3ServiceContext';
import { WXTilesV3Legend } from './wxtilesv3-service-context/WXTilesV3Legend';
import { GeojsonContext } from './geojson-context/GeojsonContext';
import { usePopups } from './usePopups';
import { LightningServiceContext } from './lightning-service-context/LightningServiceContext';
import { Timeline } from './Timeline';
import { useTimeContext } from './time-context';
import { LiveLabel, TimeLabel } from './TimeLabel';
import { ChronologicalStrikes } from './chronological-strikes';
import { MapControls } from './map-controls';
import { isLightningLayer, isWXTilesLayer, isWXTilesV3Layer } from 'layerTypeUtils';
import { LayersControl } from 'LayersControl';

const WithoutWatermark = styled.div`
    .mapboxgl-ctrl-logo {
        display: none !important;
    }
`;

const addAnchor = (layerIndex: string) => [layerIndex, anchorId].join(delimiter);

interface MapWrapProps {
    renderingForPDF?: boolean;
    userTracking?: (properties?: any) => void;
    renderingForForecast?: boolean;
    theme?: string;
}
export const MapWrap = ({ renderingForPDF, userTracking, renderingForForecast, theme }: MapWrapProps) => {
    const [map, setMap] = useState<mapboxgl.Map>();
    const themeColors = theme === 'dark' ? { text: '#ffffff', background: '#222631' } : { text: '#172344', background: '#ffffff' };
    const mapContainer = useRef<HTMLDivElement>(null);
    const platmapContexts = {
        mapbox: useContext(MapboxServiceContext),
        wxtiles: useContext(WXTilesServiceContext),
        wxtilesv3: useContext(WXTilesV3ServiceContext),
        geojson: useContext(GeojsonContext),
        lightning: useContext(LightningServiceContext),
    };
    const [lastHoverEvent, setLastHoverEvent] = useThrottleState<mapboxgl.MapMouseEvent | undefined>({ initialState: undefined, limit: 1000 });
    const { now, isLoading } = useTimeContext();
    const { settings } = useSettingsContext();
    const [displayValues, setDisplayValues] = useState<{
        [layerId: string]: number | undefined;
    }>({});
    const [mapImage, setMapImage] = useState<string>();

    const { track, isUserTrackingLoaded } = useUserTracking();

    const [disabledLayers, setDisabledLayers] = usePersistedState({
        initialState: [] as string[],
        key: 'platmap-disabled-layers',
    });

    usePopups({
        map,
        popups: settings.popups ?? [],
    });

    const [width, height] = useSize(mapContainer);
    const { onViewportChanged, onClick, onHover, onRightClick, targetDestination, viewportKey } = usePlatmapContext(); // this context is optional, by design. It only exists for any platmap consumer that needs map updates
    const zoomInteractivity = settings.isInteractive || settings.isZoomable;

    useLayoutEffect(() => {
        if (!mapContainer.current) return;

        const newMap = new mapboxgl.Map({
            accessToken: mapboxAccessTokenPk,
            //@ts-ignore
            style: baseStyle, // You need a base style, or the load event is not triggered. Use the blank style, and let agents load their own styles
            container: mapContainer.current,
            center: settings.center,
            zoom: settings.zoom,
            interactive: zoomInteractivity,
            attributionControl: false,
            dragRotate: false,
            maxBounds: settings.maxZoomBounds
                ? new mapboxgl.LngLatBounds([settings.maxZoomBounds[0], settings.maxZoomBounds[1]], [settings.maxZoomBounds[2], settings.maxZoomBounds[3]])
                : undefined,
        });

        newMap.on('load', () => {
            setMap(newMap);

            if (zoomInteractivity) {
                newMap.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
            }

            if (onViewportChanged) {
                const mapCenter = newMap.getCenter();

                const mapBounds = newMap.getBounds();
                const sw = mapBounds.getSouthWest();
                const ne = mapBounds.getNorthEast();
                onViewportChanged({
                    center: [mapCenter.lng, mapCenter.lat],
                    zoom: newMap.getZoom(),
                    bbox: [sw.lng, sw.lat, ne.lng, ne.lat],
                });
            }
        });
    }, []);

    useEffect(() => {
        if (!map) return;
        if (!settings.maxZoomBounds) {
            map.setMaxBounds(undefined);
            return;
        }
        // Convert the array to LngLatBounds
        const newBounds = new mapboxgl.LngLatBounds(
            [settings.maxZoomBounds[0], settings.maxZoomBounds[1]],
            [settings.maxZoomBounds[2], settings.maxZoomBounds[3]]
        );

        map.setMaxBounds(newBounds);

        // Calculate the center of the new maxBounds and set it as the map center
        const boundsCenter = newBounds.getCenter();
        map.setCenter([boundsCenter.lng, boundsCenter.lat]);
    }, [settings.maxZoomBounds, map]);

    useEffect(() => {
        if (!isUserTrackingLoaded) return;
        if (!userTracking) return;

        const trackZoomIn = () => userTracking({ action: 'zoom_in' });
        const trackZoomOut = () => userTracking({ action: 'zoom_out' });

        document.querySelector('button.mapboxgl-ctrl-zoom-in')?.addEventListener('mouseup', trackZoomIn);
        document.querySelector('button.mapboxgl-ctrl-zoom-out')?.addEventListener('mouseup', trackZoomOut);

        return () => {
            document.querySelector('button.mapboxgl-ctrl-zoom-in')?.removeEventListener('mouseup', trackZoomIn);
            document.querySelector('button.mapboxgl-ctrl-zoom-out')?.removeEventListener('mouseup', trackZoomOut);
        };
    }, [userTracking, isUserTrackingLoaded]);

    useEffect(() => {
        if (!map) return;
        Object.values(platmapContexts).forEach((platmapContext) => {
            platmapContext.setMap(map);
        });

        if (!renderingForPDF) return;
        const onRenderHandler = map.on('render', () => {
            const mapImage = map.getCanvas().toDataURL('image/png');
            setMapImage(mapImage);
        });

        return () => {
            onRenderHandler.remove();
        };
    }, [map]);

    useEffect(() => {
        if (!map) return;

        map.flyTo({
            zoom: settings.zoom,
            maxDuration: 16 * 60,
            center: settings.center,
        });

        const debouncedMoveEnd = debounce(() => {
            const mapCenter = map.getCenter();

            const mapBounds = map.getBounds();
            const sw = mapBounds.getSouthWest();
            const ne = mapBounds.getNorthEast();

            const zoom = map.getZoom();
            // userTracking && userTracking({ action: 'zoom', action_details: [zoom] });
            if (onViewportChanged) {
                onViewportChanged({
                    center: [mapCenter.lng, mapCenter.lat],
                    zoom,
                    bbox: [sw.lng, sw.lat, ne.lng, ne.lat],
                });
            }
        }, 1000);

        map.on('moveend', debouncedMoveEnd);

        return () => {
            map.off('moveend', debouncedMoveEnd);
        };
    }, [map, viewportKey]);

    useEffect(() => {
        if (!targetDestination) return;
        if (!map) return;
        if (settings.staticViewport) return;
        const bounds = map.getBounds();

        // if targetDestination.center is within bounds return immediately
        if (
            targetDestination.center[0] >= bounds.getWest() &&
            targetDestination.center[0] <= bounds.getEast() &&
            targetDestination.center[1] >= bounds.getSouth() &&
            targetDestination.center[1] <= bounds.getNorth()
        )
            return;

        map.flyTo({
            zoom: targetDestination.zoom,
            center: targetDestination.center,
        });
    }, [map, toHash(targetDestination)]);

    useEffect(() => {
        if (!map) return;

        const indiciesRendered = (map.getStyle().layers ?? [])
            .filter((l) => l.id.startsWith('plat'))
            .filter((l) => l.id.includes(anchorId)) // We only want to manage the anchor layers here, not the child layers, the children should follow the anchor
            .map((l) =>
                l.id
                    .split(delimiter)
                    .filter((a) => a !== anchorId)
                    .join(delimiter)
            );

        const indiciesExpected = settings.layers.map(getLayerIndex);

        const { add, remove } = diff(indiciesRendered, indiciesExpected);

        for (const layerIndex of remove) {
            map.removeLayer(addAnchor(layerIndex));
            map.removeSource(addAnchor(layerIndex));
        }

        for (const layerIndex of add) {
            map.addSource([layerIndex, anchorId].join(delimiter), {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            });
            map.addLayer({
                id: [layerIndex, anchorId].join(delimiter),
                type: 'line',
                source: [layerIndex, anchorId].join(delimiter),
            });
        }

        for (const _i in settings.layers) {
            const i = parseInt(_i);

            const layerIndex = getLayerIndex(settings.layers[i]);
            const previousLayer = settings.layers[i - 1];

            if (!previousLayer) {
                map.moveLayer(addAnchor(layerIndex));
                continue;
            }

            const id = addAnchor(layerIndex);
            const beforeId = addAnchor(getLayerIndex(previousLayer));
            map.moveLayer(id, beforeId);
        }

        if (settings.enableLayerSelection) {
            settings.layers.forEach((layer) => {
                if (isWXTilesV3Layer(layer)) {
                    const layerId = ['plat', layer.state.type, layer.id, layer.state.datasetId, layer.state.variable].join(delimiter);
                    if (disabledLayers.includes(layer.id)) {
                        map.getLayer(layerId) && map.setLayoutProperty(layerId, 'visibility', 'none');
                        return;
                    }
                    map.getLayer(layerId) && map.setLayoutProperty(layerId, 'visibility', 'visible');
                    return;
                }
                if (isLightningLayer(layer)) {
                    const lightningLayers = map.getStyle().layers?.filter((layer) => layer.id.includes('lightning'));
                    if (disabledLayers.includes('lightning')) {
                        lightningLayers?.forEach((layer) => {
                            map.setLayoutProperty(layer.id, 'visibility', 'none');
                        });
                        return;
                    }
                    lightningLayers?.forEach((layer) => {
                        map.setLayoutProperty(layer.id, 'visibility', 'visible');
                    });
                    return;
                }
            });
        } else {
            setDisabledLayers([]);
            map.getStyle().layers?.forEach((layer) => {
                if (layer.id.includes('lightning') || layer.id.includes('wxtilesv3')) map.setLayoutProperty(layer.id, 'visibility', 'visible');
            });
        }

        const shuffleIndex = toHash([settings.layers, add, remove, disabledLayers]);
        for (const platmapContext of Object.values(platmapContexts)) {
            platmapContext.shuffle(shuffleIndex);
        }

        const _onClick = (e: mapboxgl.MapMouseEvent) => {
            if (!onClick && !onRightClick) return;
            const features = map.queryRenderedFeatures(e.point);

            const normalizedPoint: [number, number] = [normalizeLongitude180(e.lngLat.lng), e.lngLat.lat];
            const normalizedCoordinates: { lat: number; lon: number } = {
                lat: e.lngLat.lat,
                lon: normalizeLongitude360(e.lngLat.lng),
            };

            const wxtilesV3Values = settings.layers.filter(isWXTilesV3Layer).map((layer) => {
                const source = platmapContexts.wxtilesv3.wxSources[layer.id];
                if (!source) return { layerId: layer.id };
                const lngLat = new mapboxgl.LngLat(normalizedPoint[0], normalizedPoint[1]);
                return {
                    layerId: layer.id,
                    datum: source.getLayerInfoAtLatLon(lngLat, map)?.inStyleUnits,
                };
            });

            if (e.type === 'contextmenu') {
                onRightClick?.({
                    features,
                    normalizedPoint,
                    wxtilesV3Values,
                    coordinates: normalizedCoordinates,
                    mouseCoordinates: e.point,
                });
                return;
            }

            onClick?.({
                features,
                normalizedPoint,
                wxtilesV3Values,
                coordinates: normalizedCoordinates,
                mouseCoordinates: e.point,
            });
        };

        const _onHover = (e: mapboxgl.MapMouseEvent) => {
            if (!onHover) return;
            setLastHoverEvent(e);
        };

        map.on('click', _onClick);
        map.on('contextmenu', _onClick);
        map.on('mousemove', _onHover);
        return () => {
            map.off('click', _onClick);
            map.off('contextmenu', _onClick);
            map.off('mousemove', _onHover);
        };
    }, [toHash(settings.layers), map, Object.keys(platmapContexts.wxtilesv3.wxSources).length, settings.enableLayerSelection, disabledLayers.length]);

    useEffect(() => {
        if (!onHover || !map || !lastHoverEvent) return;
        const features = map.queryRenderedFeatures(lastHoverEvent.point);
        const normalizedPoint: [number, number] = [normalizeLongitude180(lastHoverEvent.lngLat.lng), lastHoverEvent.lngLat.lat];
        const normalizedCoordinates: { lat: number; lon: number } = {
            lat: lastHoverEvent.lngLat.lat,
            lon: normalizeLongitude360(lastHoverEvent.lngLat.lng),
        };

        const wxtilesV3Values = settings.layers.filter(isWXTilesV3Layer).map((layer) => {
            const source = platmapContexts.wxtilesv3.wxSources[layer.id];
            if (!source) return { layerId: layer.id };
            const lngLat = new mapboxgl.LngLat(normalizedPoint[0], normalizedPoint[1]);
            return {
                layerId: layer.id,
                datum: source.getLayerInfoAtLatLon(lngLat, map)?.inStyleUnits,
            };
        });

        setDisplayValues(Object.fromEntries(wxtilesV3Values.map((v) => [v.layerId, v.datum ? Math.round(v.datum[0] * 100) / 100 : undefined])));
        onHover({
            features,
            normalizedPoint,
            wxtilesV3Values,
            coordinates: normalizedCoordinates,
            mouseCoordinates: lastHoverEvent.point,
        });
    }, [lastHoverEvent]);

    useDebouncedEffect(
        () => {
            if (!map) return;
            map.resize();
        },
        { ignoreInitialCall: true, timeout: 32 },
        [width, height]
    );

    const outerWidth = Math.min(width - 16, 450);

    const isLightningBoxEnabled =
        settings.timeControl.type === 'livemode' && settings.layers.some((l) => l.state.type === 'lightning' && l.state.showChronologicalStrikes === true);

    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    flexGrow: 1,
                    display: 'flex',
                    position: 'relative',
                    maxHeight: '100%',
                }}
            >
                <WithoutWatermark id='MapWrap' ref={mapContainer} style={{ width: '100%', height: '100%' }} />
                {renderingForPDF && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                        }}
                    >
                        <img src={mapImage} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        position: 'absolute',
                        flexDirection: 'column',
                        right: 12,
                        top: 112,
                        zIndex: 999,
                    }}
                >
                    {zoomInteractivity && <MapControls />}
                    {settings.enableLayerSelection && <LayersControl disabledLayers={disabledLayers} setDisabledLayers={setDisabledLayers} />}
                </div>
                <div
                    style={{
                        position: 'absolute',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        ...(settings.legendsPosition ?? {
                            bottom: 80,
                            right: 8,
                        }),
                    }}
                >
                    {settings.layers
                        .filter(isWXTilesLayer)
                        .map((layer) => {
                            const wxtilesService = platmapContexts['wxtiles'];
                            if (!wxtilesService.layers) return null;
                            const { wxtilesLayerId, styleId } = layer.state;

                            const wxtilesLayer = wxtilesService.layers.find((l) => l.id === wxtilesLayerId);
                            if (!wxtilesLayer) return null;

                            const wxtilesLayerStyle = wxtilesLayer.styles.find((s) => styleId === s.id);
                            if (!wxtilesLayerStyle) return null;
                            if (!wxtilesLayerStyle.resources.legend) return null;

                            return {
                                layer,
                                legendUrl: getLegendUrl({
                                    wxtilesEndpoint: settings.wxtiles.serviceEndpoint,
                                    urlTemplate: wxtilesLayerStyle.resources.legend,
                                }),
                                wxtilesLabel: wxtilesLayer.name,
                            };
                        })
                        .filter(
                            (
                                a
                            ): a is {
                                layer: Layer & { state: WXTilesLayer };
                                legendUrl: string;
                                wxtilesLabel: string;
                            } => !!a
                        )
                        .map(({ layer, legendUrl, wxtilesLabel }) => {
                            return (
                                <div
                                    key={layer.id}
                                    style={{
                                        boxShadow: '0 10px 20px rgb(0 0 0 / 19%), 0 6px 6px rgb(0 0 0 / 23%)',
                                        backgroundColor: 'rgb(220, 233, 240)',
                                        padding: 4,
                                        borderRadius: 6,
                                    }}
                                >
                                    <div
                                        style={{
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '0.9em',
                                        }}
                                    >
                                        {wxtilesLabel}
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <img src={legendUrl} width={settings.wxtiles.legendSize} />
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>

            <div
                style={{
                    position: 'absolute',
                    top: 10,
                    right: renderingForForecast ? 40 : 0,
                    width: '100%',
                    pointerEvents: 'none',
                }}
            >
                <div
                    style={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 8,
                        width: '100%',
                        justifyContent: isLightningBoxEnabled ? 'space-between' : 'end',
                        alignItems: 'start',
                        paddingInline: '8px',
                        flexWrap: 'wrap-reverse',
                        paddingRight: 60,
                    }}
                >
                    {isLightningBoxEnabled && <ChronologicalStrikes maxHeight={height} theme={themeColors} />}

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                        }}
                    >
                        {settings.layers.map((layer) => {
                            if (layer.state.type === 'wxtilesv3' && layer.state.showLegend && !disabledLayers.includes(layer.id))
                                return (
                                    <WXTilesV3Legend
                                        key={layer.id}
                                        wxtilesV3State={layer.state}
                                        displayValue={displayValues[layer.id]}
                                        outerWidth={outerWidth}
                                    />
                                );
                            if (
                                layer.state.type === 'lightning' &&
                                (layer.state.showLegend === undefined || layer.state.showLegend) &&
                                !disabledLayers.includes(layer.id)
                            )
                                return (
                                    <ColorMapLegend
                                        key={layer.id}
                                        colorMap={[
                                            [0, '#F10707'],
                                            [20, '#FFE600'],
                                            [40, '#39FF69'],
                                            [60, '#00A3FF'],
                                        ]}
                                        units={'min'}
                                        marks={[0, 5, 10, 15, 20, 30, 45, 60]}
                                        evenlyLabelMarks
                                        outerContainerWidth={outerWidth}
                                    />
                                );

                            return null;
                        })}
                    </div>
                </div>
            </div>

            {settings.topLabel && (
                <div
                    style={{
                        position: 'absolute',
                        top: 12,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <div
                        style={{
                            backgroundColor: 'rgb(220, 233, 240)',
                            boxShadow: '0 10px 20px rgb(0 0 0 / 19%), 0 6px 6px rgb(0 0 0 / 23%)',
                            padding: 8,
                            borderRadius: 8,
                        }}
                    >
                        {settings.timeOffset
                            ? settings.topLabel
                                  .split('{datetime}')
                                  .join(
                                      DateTime.fromISO(now, { zone: settings.timezone })
                                          .plus(Duration.fromISO(settings.timeOffset))
                                          .toFormat('yyyy-MM-dd HH:mm (Z)')
                                  )
                            : settings.topLabel}
                    </div>
                </div>
            )}

            {settings.timeControl.type === 'timespan' && (
                <div style={{ position: 'absolute', bottom: 0, width: '100%' }}>
                    <Timeline />
                </div>
            )}
            {settings.timeControl.type === 'timeoffset' && !settings.timeControl.hideTimeLabel && (
                <div style={{ position: 'absolute', bottom: 12, left: 12 }}>
                    <TimeLabel />
                </div>
            )}
            {settings.timeControl.type === 'livemode' && (
                <div style={{ position: 'absolute', bottom: 12, left: 12 }}>
                    <LiveLabel />
                </div>
            )}
            {Object.values(isLoading).some(Boolean) && !settings.hideLoading && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 8,
                        backgroundColor: 'white',
                        height: 30,
                        borderRadius: 30,
                        padding: '4px 16px',
                        alignItems: 'center',
                    }}
                >
                    <LoadingOutlined style={{ fontSize: 14 }} />
                    <div style={{ fontSize: 16 }}>Loading</div>
                </div>
            )}
        </div>
    );
};
